import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import {
  TextField, TextArea, SelectField, RadioRow, CheckboxGrid, SectionCard, FieldLabel, cx,
} from "@/components/FormFields";
import {
  BUSINESS_TYPES, YEARS_IN_BUSINESS, PRODUCTS, QUANTITY_TIERS,
  ORDER_TIMING, PURCHASE_TYPE, RECEIVING_LOCATIONS,
} from "@/lib/constants";

const initial = {
  business_name: "", contact_name: "", email: "", phone: "", business_address: "",
  city: "", state: "", zip_code: "", website: "", social_media: "", business_type: "",
  sells_ebikes: "", current_brands: "", physical_location: "", sells_online: "",
  years_in_business: "", products_interested: [], quantity_tier: "", recurring_orders: "",
  interested_specific_models: "", models_list: "", color_specs: "", custom_branding: "",
  first_order_timing: "", purchase_type: "", shipping_city: "", shipping_state: "",
  shipping_zip: "", receiving_location: "", forklift_access: "", additional_info: "",
  company_url: "",
};

const REQUIRED = [
  "business_name", "contact_name", "email", "phone", "business_address", "city",
  "state", "zip_code", "business_type", "sells_ebikes", "physical_location",
  "sells_online", "years_in_business", "quantity_tier", "recurring_orders",
  "interested_specific_models", "custom_branding", "first_order_timing",
  "purchase_type", "shipping_city", "shipping_state", "shipping_zip",
  "receiving_location", "forklift_access",
];

const MoqBadge = ({ className = "" }) => (
  <span
    className={cx(
      "inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border border-[#8CE63C]/40 text-[#8CE63C] bg-[#132717]",
      className
    )}
    data-testid="moq-callout-banner"
  >
    <span className="h-1.5 w-1.5 rounded-full bg-[#8CE63C] animate-pulse" />
    Minimum Order: 10 Units
  </span>
);

export default function DealerForm() {
  const [form, setForm] = useState(initial);
  const [captcha, setCaptcha] = useState({ question: "", token: "" });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { reference_id }
  const [errors, setErrors] = useState({});
  const [catalog, setCatalog] = useState(null);

  const loadCaptcha = () => {
    api.get("/captcha").then((r) => setCaptcha(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadCaptcha();
    api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const viewCatalog = async () => {
    try {
      const res = await api.get("/catalog/download", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open the catalog. Please try again.");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleProduct = (p) =>
    setForm((f) => ({
      ...f,
      products_interested: f.products_interested.includes(p)
        ? f.products_interested.filter((x) => x !== p)
        : [...f.products_interested, p],
    }));

  const validate = () => {
    const errs = {};
    REQUIRED.forEach((k) => {
      if (!String(form[k] || "").trim()) errs[k] = "Required";
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = "Enter a valid email address";
    if (form.phone && form.phone.replace(/\D/g, "").length < 7)
      errs.phone = "Enter a valid phone number";
    if (form.products_interested.length === 0) errs.products_interested = "Select at least one";
    if (!captchaAnswer.trim()) errs.captcha = "Answer the verification question";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please complete all required fields.");
      const firstEl = document.querySelector(`[data-error="true"]`);
      if (firstEl) firstEl.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/dealers", {
        ...form,
        captcha_token: captcha.token,
        captcha_answer: captchaAnswer,
      });
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
      loadCaptcha();
      setCaptchaAnswer("");
    } finally {
      setSubmitting(false);
    }
  };

  const errProps = (k) => ({ error: errors[k], "data-error": errors[k] ? "true" : undefined });

  if (done) {
    return (
      <Layout>
        <div className="flex items-center justify-center px-4 py-16">
          <div className="erp-fade-up max-w-xl w-full text-center rounded-2xl border border-[#232f25] bg-[#111712] p-8 sm:p-12">
            <div className="mx-auto h-16 w-16 rounded-full bg-[#132717] border border-[#8CE63C] flex items-center justify-center erp-lime-glow">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#8CE63C" strokeWidth="3">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-white uppercase tracking-tight mt-6">
              Thank You!
            </h1>
            <p className="text-[#9EABA0] text-sm sm:text-base leading-relaxed mt-4">
              Your dealer request has been received. Our team will review your information and
              contact you with available products, wholesale pricing, and next steps.
            </p>
            <div className="mt-6 inline-flex flex-col items-center">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#69776b]">
                Reference Number
              </span>
              <span
                data-testid="confirmation-reference-id"
                className="font-mono text-xl font-bold text-[#8CE63C] mt-1"
              >
                {done.reference_id}
              </span>
            </div>
            <p className="text-xs text-[#69776b] mt-4">
              A confirmation email has been sent to <strong className="text-[#9EABA0]">{form.email}</strong>.
            </p>
            <Link
              to="/"
              data-testid="confirmation-return-home-button"
              onClick={() => {
                setDone(null);
                setForm(initial);
                setCaptchaAnswer("");
                loadCaptcha();
              }}
              className="inline-block mt-8 px-6 py-3 rounded-md bg-[#0d120e] border border-[#232f25] text-sm font-semibold text-[#f4f7f4] hover:border-[#8CE63C]/50 transition-colors"
            >
              Submit Another Request
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="erp-grid-bg">

      {/* Hero */}
      <header className="relative overflow-hidden border-b border-[#232f25]">
        <img
          src="https://images.unsplash.com/photo-1660337294765-2a20770826aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0d0a]/70 via-[#0a0d0a]/85 to-[#0a0d0a]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#8CE63C]">
            Wholesale · Dealer Program
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-tight mt-4">
            Become an E Ride <br className="hidden sm:block" />
            <span className="text-[#8CE63C]">Power Sports</span> Dealer
          </h1>
          <p className="text-[#9EABA0] text-sm sm:text-base leading-relaxed max-w-2xl mt-5">
            Interested in purchasing e-bikes or electric powersports products in bulk? Complete the
            form below and our team will review your request and contact you with available models,
            wholesale pricing, minimum order requirements, and shipping options.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <MoqBadge />
            <a
              href="#dealer-form"
              className="px-5 py-2.5 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-bold uppercase tracking-wide hover:bg-[#9df04f] transition-colors"
            >
              Start Application
            </a>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {catalog?.available && (
          <div
            data-testid="catalog-download-banner"
            className="erp-fade-up mb-8 rounded-xl border border-[#8CE63C]/40 bg-[#111712] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          >
            <div className="flex items-start gap-4">
              <span className="h-12 w-12 rounded-md bg-[#132717] border border-[#8CE63C]/40 flex items-center justify-center text-[#8CE63C] font-mono text-xs font-bold shrink-0">
                PDF
              </span>
              <div>
                <h3 className="font-display text-lg font-bold text-white tracking-tight">Current Inventory Catalog</h3>
                <p className="text-xs text-[#9EABA0] mt-1 max-w-md leading-relaxed">
                  Browse our current availability of e-bikes and electric powersports models before you apply.
                  {catalog.updated_at && (
                    <span className="text-[#69776b]">
                      {" "}Updated {new Date(catalog.updated_at).toLocaleDateString()}.
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              type="button"
              data-testid="catalog-download-button"
              onClick={viewCatalog}
              className="shrink-0 px-5 py-2.5 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-bold uppercase tracking-wide hover:bg-[#9df04f] transition-colors"
            >
              View Catalog PDF
            </button>
          </div>
        )}
        <form id="dealer-form" data-testid="dealer-interest-form" onSubmit={submit} className="space-y-6">
          {/* honeypot */}
          <input
            type="text"
            name="company_url"
            value={form.company_url}
            onChange={set("company_url")}
            tabIndex={-1}
            autoComplete="off"
            className="hidden"
            aria-hidden="true"
          />

          <SectionCard index={1} title="Business Information">
            <div className="grid sm:grid-cols-2 gap-5">
              <TextField label="Business Name" required testId="input-business-name" value={form.business_name} onChange={set("business_name")} {...errProps("business_name")} />
              <TextField label="Contact Name" required testId="input-contact-name" value={form.contact_name} onChange={set("contact_name")} {...errProps("contact_name")} />
              <TextField label="Email Address" required type="email" testId="input-email" value={form.email} onChange={set("email")} {...errProps("email")} />
              <TextField label="Phone Number" required type="tel" testId="input-phone" value={form.phone} onChange={set("phone")} {...errProps("phone")} />
              <div className="sm:col-span-2">
                <TextField label="Business Address" required testId="input-business-address" value={form.business_address} onChange={set("business_address")} {...errProps("business_address")} />
              </div>
              <TextField label="City" required testId="input-city" value={form.city} onChange={set("city")} {...errProps("city")} />
              <TextField label="State" required testId="input-state" value={form.state} onChange={set("state")} {...errProps("state")} />
              <TextField label="ZIP Code" required testId="input-zip" value={form.zip_code} onChange={set("zip_code")} {...errProps("zip_code")} />
              <TextField label="Business Website" testId="input-website" placeholder="https://" value={form.website} onChange={set("website")} />
              <div className="sm:col-span-2">
                <TextField label="Social Media / Business Page" testId="input-social" value={form.social_media} onChange={set("social_media")} />
              </div>
              <div className="sm:col-span-2">
                <SelectField label="What type of business do you operate?" required testId="select-business-type" options={BUSINESS_TYPES} value={form.business_type} onChange={set("business_type")} {...errProps("business_type")} />
              </div>
            </div>
          </SectionCard>

          <SectionCard index={2} title="Dealer Experience">
            <RadioRow label="Do you currently sell e-bikes or powersports products?" required options={["Yes", "No"]} value={form.sells_ebikes} onChange={setVal("sells_ebikes")} testIdPrefix="radio-sells-ebikes" />
            {form.sells_ebikes === "Yes" && (
              <div className="erp-fade-up">
                <TextField label="What brands/products do you currently carry?" testId="input-current-brands" value={form.current_brands} onChange={set("current_brands")} />
              </div>
            )}
            <RadioRow label="Do you have a physical retail location?" required options={["Yes", "No"]} value={form.physical_location} onChange={setVal("physical_location")} testIdPrefix="radio-physical-location" />
            <RadioRow label="Do you currently sell products online?" required options={["Yes", "No"]} value={form.sells_online} onChange={setVal("sells_online")} testIdPrefix="radio-sells-online" />
            <SelectField label="How long have you been in business?" required testId="select-years-operating" options={YEARS_IN_BUSINESS} value={form.years_in_business} onChange={set("years_in_business")} {...errProps("years_in_business")} />
          </SectionCard>

          <SectionCard index={3} title="Products You're Interested In" desc="Select all that apply.">
            <FieldLabel required>What products are you interested in purchasing?</FieldLabel>
            <CheckboxGrid options={PRODUCTS} selected={form.products_interested} onToggle={toggleProduct} testIdPrefix="checkbox-product-" />
            {errors.products_interested && <p className="text-[11px] text-red-400">{errors.products_interested}</p>}
          </SectionCard>

          <SectionCard index={4} title="Quantity">
            <div className="grid sm:grid-cols-2 gap-5">
              <SelectField label="How many units initially?" required testId="select-quantity-tier" options={QUANTITY_TIERS} value={form.quantity_tier} onChange={set("quantity_tier")} {...errProps("quantity_tier")} />
            </div>
            <MoqBadge />
            <RadioRow label="Are you interested in recurring/ongoing wholesale orders?" required options={["Yes", "No", "Not sure"]} value={form.recurring_orders} onChange={setVal("recurring_orders")} testIdPrefix="radio-recurring" />
          </SectionCard>

          <SectionCard index={5} title="Product Preferences">
            <RadioRow label="Are you interested in specific models?" required options={["Yes", "No", "Not sure"]} value={form.interested_specific_models} onChange={setVal("interested_specific_models")} testIdPrefix="radio-specific-models" />
            {form.interested_specific_models === "Yes" && (
              <div className="erp-fade-up">
                <TextField label="List the models you're interested in" testId="input-models-list" value={form.models_list} onChange={set("models_list")} />
              </div>
            )}
            <TextField label="Preferred colors / specifications" testId="input-color-specs" value={form.color_specs} onChange={set("color_specs")} />
            <RadioRow label="Are you interested in custom branding / private labeling?" required options={["Yes", "No", "Maybe"]} value={form.custom_branding} onChange={setVal("custom_branding")} testIdPrefix="radio-custom-branding" />
          </SectionCard>

          <SectionCard index={6} title="Purchase Timeline">
            <SelectField label="When are you looking to place your first order?" required testId="select-timeline" options={ORDER_TIMING} value={form.first_order_timing} onChange={set("first_order_timing")} {...errProps("first_order_timing")} />
            <RadioRow label="One-time purchase or long-term dealer?" required options={PURCHASE_TYPE} value={form.purchase_type} onChange={setVal("purchase_type")} testIdPrefix="radio-purchase-type" />
          </SectionCard>

          <SectionCard index={7} title="Shipping Information">
            <FieldLabel required>Where will the products be delivered?</FieldLabel>
            <div className="grid sm:grid-cols-3 gap-5">
              <TextField label="City" required testId="input-ship-city" value={form.shipping_city} onChange={set("shipping_city")} {...errProps("shipping_city")} />
              <TextField label="State" required testId="input-ship-state" value={form.shipping_state} onChange={set("shipping_state")} {...errProps("shipping_state")} />
              <TextField label="ZIP Code" required testId="input-ship-zip" value={form.shipping_zip} onChange={set("shipping_zip")} {...errProps("shipping_zip")} />
            </div>
            <SelectField label="What type of receiving location do you have?" required testId="select-dock-type" options={RECEIVING_LOCATIONS} value={form.receiving_location} onChange={set("receiving_location")} {...errProps("receiving_location")} />
            <RadioRow label="Do you have access to a forklift or unloading equipment?" required options={["Yes", "No", "Not sure"]} value={form.forklift_access} onChange={setVal("forklift_access")} testIdPrefix="radio-forklift" />
          </SectionCard>

          <SectionCard index={8} title="Additional Information">
            <TextArea
              label="Tell us more about what you're looking for"
              testId="textarea-territory-request"
              rows={6}
              value={form.additional_info}
              onChange={set("additional_info")}
              placeholder="Tell us which products, quantities, models, specifications, target price range, or anything else that would help us prepare your dealer quote."
            />
          </SectionCard>

          {/* Captcha + submit */}
          <div className="rounded-xl border border-[#232f25] bg-[#111712] p-5 sm:p-7 space-y-5">
            <div className="max-w-xs">
              <FieldLabel required>Verification — {captcha.question || "…"}</FieldLabel>
              <input
                data-testid="captcha-answer-input"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                inputMode="numeric"
                placeholder="Your answer"
                className={cx(
                  "w-full bg-[#0d120e] border rounded-md px-3.5 py-2.5 text-sm text-[#f4f7f4] placeholder:text-[#69776b] outline-none focus:border-[#8CE63C] focus:ring-2 focus:ring-[#8CE63C]/20",
                  errors.captcha ? "border-red-500/70" : "border-[#232f25]"
                )}
              />
              {errors.captcha && <p className="text-[11px] text-red-400 mt-1">{errors.captcha}</p>}
            </div>
            <p className="text-xs text-[#69776b] leading-relaxed">
              By submitting, you acknowledge the 10-unit minimum order quantity. Submitting this form
              does not commit you to any purchase. Wholesale pricing is provided after our team reviews
              your requested products and quantities.
            </p>
            <button
              type="submit"
              data-testid="submit-dealer-application-button"
              disabled={submitting}
              className="w-full sm:w-auto px-8 py-3.5 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-black uppercase tracking-wider hover:bg-[#9df04f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting…" : "Submit Dealer Request"}
            </button>
          </div>
        </form>
      </main>
    </div>
    </Layout>
  );
}
